import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getEmployeeProducts,
  getEmployeeProduct,
  createProduct,
  updateProduct,
  reactivateProduct as reactivateProductService,
  deleteProduct as deleteProductService,
  getProductChoices,
  bulkSetCategory as bulkSetCategoryService,
  setInventoryStatus as setInventoryStatusService,
} from '../services/products';
import { logError } from '../utils/errorHandler';

/**
 * Custom hook for managing employee product CRUD state
 *
 * @param {Object} options
 * @param {boolean} options.autoFetch - Fetch products + choices on mount (default: true)
 * @returns {Object} State and action functions
 */
const useEmployeeProducts = ({ autoFetch = true } = {}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(autoFetch);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ count: 0, page: 1 });
  const [choices, setChoices] = useState(null);
  const hasLoadedRef = useRef(false);

  const fetchProducts = useCallback(async (params = {}) => {
    try {
      setError(null);
      if (hasLoadedRef.current) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const data = await getEmployeeProducts(params);
      setProducts(data.results || []);
      setPagination({
        count: data.count || 0,
        page: params.page || 1,
      });
    } catch (err) {
      logError('Fetch products', err);
      setError(err);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const fetchProduct = useCallback(async (id) => {
    try {
      setError(null);
      setLoading(true);
      const data = await getEmployeeProduct(id);
      return data;
    } catch (err) {
      logError('Fetch product', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveProduct = useCallback(async (data, id = null) => {
    try {
      setError(null);
      setIsSaving(true);
      const result = id
        ? await updateProduct(id, data)
        : await createProduct(data);
      return result;
    } catch (err) {
      logError('Save product', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const removeProduct = useCallback(async (id) => {
    try {
      setError(null);
      setIsDeleting(true);
      await deleteProductService(id);
    } catch (err) {
      logError('Delete product', err);
      setError(err);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  const reactivateProduct = useCallback(async (id) => {
    try {
      setError(null);
      setIsSaving(true);
      await reactivateProductService(id);
    } catch (err) {
      logError('Reactivate product', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const setInventoryStatus = useCallback(async (id, inventoryStatus) => {
    try {
      setError(null);
      setIsSaving(true);
      const updated = await setInventoryStatusService(id, inventoryStatus);
      // Update the single product in-place rather than triggering a full refetch
      setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      return updated;
    } catch (err) {
      logError('Set inventory status', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const bulkSetCategory = useCallback(async (productIds, category) => {
    try {
      setError(null);
      setIsSaving(true);
      const result = await bulkSetCategoryService(productIds, category);
      return result;
    } catch (err) {
      logError('Bulk set category', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const fetchChoices = useCallback(async () => {
    try {
      const data = await getProductChoices();
      setChoices(data);
    } catch (err) {
      logError('Fetch product choices', err);
    }
  }, []);

  const refresh = useCallback((params = {}) => {
    return fetchProducts(params);
  }, [fetchProducts]);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (autoFetch) {
      fetchProducts();
      fetchChoices();
    }
  }, [autoFetch, fetchProducts, fetchChoices]);

  return {
    products,
    loading,
    isRefreshing,
    isSaving,
    isDeleting,
    error,
    pagination,
    choices,
    fetchProducts,
    fetchProduct,
    saveProduct,
    removeProduct,
    reactivateProduct,
    setInventoryStatus,
    bulkSetCategory,
    fetchChoices,
    refresh,
    clearError,
  };
};

export default useEmployeeProducts;
